import { isArray } from "lodash-es";
import type { IToggle } from "unleash-proxy-client";

import type { UnknownRecord } from "../type";
import { featureToggles } from "./featureToggles";

interface Feature {
  id: string;
  description: string;
  active?: boolean;
  fromGitlab?: boolean;
}

const initialFeatures = [
  {
    id: "explorer",
    description: "Breakdown pivot grid + Map",
  },
  { id: "nats-subscribe", description: "Use the generated NATS subscribe stubs" },
  { id: "sightline", description: "Sightline: AI answers about the session's health" },
  {
    id: "nats-piweb",
    description: "Use generated NATS RPC stubs (pi-web direct, bypass pi-bridge)",
  },
  { id: "graphdb", description: "Graph database projection (required for graphdb-served reads)" },
  {
    id: "graphdb-served",
    description: "Multi-hop status distribution on solution-backlog workitems and ART objectives",
  },
  { id: "signals", description: "Signals: plan-risk highlights in the top menu and search" },
] as const satisfies readonly Feature[];

export type FeatureId = (typeof initialFeatures)[number]["id"];

let features: Feature[] = initialFeatures.map((f) => ({ ...f }));
let gitlabToggles: IToggle[] | null = null;

export function isFeatureEnabled(route: { query: UnknownRecord }, featureId: FeatureId): boolean {
  const found = features.find((f) => f.id === featureId);
  if (found?.active !== undefined) return found.active;

  return (
    !isFeatureEnabledInQuery(route, "!" + featureId) &&
    (isFeatureEnabledInQuery(route, featureId) || featureToggles.isEnabled(featureId))
  );
}

// Mirrors the server's `serve_from_graph` (graphdb AND graphdb-served) so a
// distribution bar is only shown when the server will actually serve it.
export function isStatusDistributionFromGraph(route: { query: UnknownRecord }): boolean {
  return isFeatureEnabled(route, "graphdb") && isFeatureEnabled(route, "graphdb-served");
}

export function isSignalsEnabled(): boolean {
  return isFeatureEnabled({ query: parseUrlWithoutRouter() }, "signals");
}

// Graph signals need the signals flag and the server's graph serving flags.
export function isGraphSignalsEnabled(): boolean {
  return isSignalsEnabled() && isStatusDistributionFromGraph({ query: parseUrlWithoutRouter() });
}

export function parseUrlWithoutRouter() {
  const hash = window.location.hash;
  const queryStart = hash.indexOf("?");
  if (queryStart < 0) {
    return {};
  }
  const params = new URLSearchParams(hash.substring(queryStart));
  const extractedParams: Record<string, string[]> = {};
  for (const [key, value] of params) {
    if (extractedParams[key]) {
      extractedParams[key].push(value);
    } else {
      extractedParams[key] = [value];
    }
  }
  return extractedParams;
}

function isFeatureEnabledInQuery(route: { query: UnknownRecord }, feature: string): boolean {
  if (!route?.query?.feature) {
    return false;
  }
  if (isArray(route.query.feature)) {
    return route.query.feature.includes(feature);
  }
  return route.query.feature === feature;
}

export function broadcastFeatures() {
  window.addEventListener("extensionAvailable", broadcastFeatureState);
  window.addEventListener("message", updateFeatures);
}

export function setGitlabToggles(toggles: IToggle[]) {
  gitlabToggles = toggles;
  broadcastGitlabToggles(toggles);
}

function broadcastFeatureState() {
  window.postMessage({ type: "SET_AVAILABLE_FEATURES", features }, "*");
  if (gitlabToggles) broadcastGitlabToggles(gitlabToggles);
}

function broadcastGitlabToggles(toggles: IToggle[]) {
  window.postMessage({ type: "SET_GITLAB_TOGGLED_FEATURES", toggles }, "*");
}

function updateFeatures(event: MessageEvent) {
  if (event.source !== window || event.data?.type !== "FEATURES_UPDATED") return;

  features = event.data.features;
  void triggerSoftRefresh();
}

async function triggerSoftRefresh() {
  const { router } = await import("@/router/router");
  const currentRoute = router?.currentRoute?.value;
  if (currentRoute && currentRoute.fullPath !== "/") {
    void router.replace({ ...currentRoute, query: { ...currentRoute.query, _t: Date.now() } });
  }
}
